import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import CurrencySelect from '../components/CurrencySelect';
import '../components/brand.css';
import AppHeader from '../components/AppHeader';
import './CreateOpportunity.css';

const XIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);

export default function CreateOpportunity() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [subjects, setSubjects] = useState([]);
  const [states, setStates] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCurrencySelect, setShowCurrencySelect] = useState(false);

  // Form state
  const [form, setForm] = useState({
    type: 'job',
    org_name: '',
    org_type: 'company',
    title: '',
    description: '',
    required_skills: [],
    skillInput: '',
    education_level: '',
    majors: [],
    interests: [],
    interestInput: '',
    location_type: 'onsite',
    state_key: '',
    duration: '',
    is_paid: false,
    stipend: '',
    currency_code: 'SDG',
    apply_method: 'in_app',
    apply_link: '',
    deadline: ''
  });

  useEffect(() => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }
    fetchSubjects();
    fetchStates();
    fetchOrgTypes();
  }, [user, token, navigate]);

  const fetchSubjects = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/subjects`);
      if (res.ok) setSubjects(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/states`);
      if (res.ok) setStates(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchOrgTypes = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/org-types`);
      if (res.ok) setOrgTypes(await res.json());
    } catch (e) { console.error(e); }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const addSkill = () => {
    if (form.skillInput.trim() && !form.required_skills.includes(form.skillInput.trim())) {
      setForm(prev => ({
        ...prev,
        required_skills: [...prev.required_skills, prev.skillInput.trim()],
        skillInput: ''
      }));
      setIsDirty(true);
    }
  };

  const removeSkill = (skill) => {
    setForm(prev => ({
      ...prev,
      required_skills: prev.required_skills.filter(s => s !== skill)
    }));
    setIsDirty(true);
  };

  const addInterest = () => {
    if (form.interestInput.trim() && !form.interests.includes(form.interestInput.trim())) {
      setForm(prev => ({
        ...prev,
        interests: [...prev.interests, prev.interestInput.trim()],
        interestInput: ''
      }));
      setIsDirty(true);
    }
  };

  const removeInterest = (interest) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest)
    }));
    setIsDirty(true);
  };

  const toggleMajor = (majorKey) => {
    setForm(prev => ({
      ...prev,
      majors: prev.majors.includes(majorKey)
        ? prev.majors.filter(m => m !== majorKey)
        : [...prev.majors, majorKey]
    }));
    setIsDirty(true);
  };

  const handleBack = () => {
    if (isDirty) {
      setShowExitDialog(true);
    } else if (hasInAppHistory()) {
      navigate(-1);
    } else {
      navigate('/student-hub', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.org_name || !form.title || !form.description) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        org_name: form.org_name,
        org_type: form.org_type,
        title: form.title,
        description: form.description,
        required_skills: form.required_skills,
        education_level: form.education_level || null,
        majors: form.majors,
        interests: form.interests,
        location_type: form.location_type,
        state_key: form.state_key || null,
        duration: form.duration || null,
        is_paid: form.is_paid,
        stipend: form.is_paid && form.stipend ? parseFloat(form.stipend) : null,
        currency_code: form.currency_code,
        apply_method: form.apply_method,
        apply_link: form.apply_method === 'external' ? form.apply_link : null,
        deadline: form.deadline || null
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        navigate(`/student-hub/opportunities/${data.opp_id}`);
      }
    } catch (e) { 
      console.error(e); 
    }
    setSubmitting(false);
  };

  return (
    <div className="create-opp-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="create-opportunity">
      <AppHeader
        title={t('opps.createTitle')}
        onBack={handleBack}
        testId="create-opp-header"
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="create-opp-form">
        {/* Type & Org */}
        <div className="form-section">
          <div className="form-group">
            <Label>{t('opps.fields.type')} *</Label>
            <div className="type-toggle">
              {['job', 'volunteer', 'internship'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`type-btn ${form.type === type ? `active type-${type}` : ''}`}
                  onClick={() => updateForm('type', type)}
                >
                  {t(`opps.type.${type}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="org_name">{t('opps.fields.orgName')} *</Label>
            <Input
              id="org_name"
              value={form.org_name}
              onChange={(e) => updateForm('org_name', e.target.value)}
              placeholder={t('opps.fields.orgName')}
              required
            />
          </div>

          <div className="form-group">
            <Label>{t('opps.fields.orgType')} *</Label>
            <Select value={form.org_type} onValueChange={(v) => updateForm('org_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {orgTypes.map(o => (
                  <SelectItem key={o.key} value={o.key}>
                    {lang === 'ar' ? o.name_ar : o.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Role Info */}
        <div className="form-section">
          <div className="form-group">
            <Label htmlFor="title">{t('opps.fields.title')} *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => updateForm('title', e.target.value)}
              placeholder={t('opps.fields.title')}
              required
            />
          </div>

          <div className="form-group">
            <Label htmlFor="description">{t('opps.fields.description')} *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              placeholder={t('opps.fields.description')}
              rows={5}
              required
            />
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <div className="form-group">
            <Label>{t('opps.fields.locationType')}</Label>
            <div className="type-toggle">
              {['onsite', 'remote', 'hybrid'].map(loc => (
                <button
                  key={loc}
                  type="button"
                  className={`type-btn ${form.location_type === loc ? 'active' : ''}`}
                  onClick={() => updateForm('location_type', loc)}
                >
                  {t(`opps.locationType.${loc}`)}
                </button>
              ))}
            </div>
          </div>

          {form.location_type !== 'remote' && (
            <div className="form-group">
              <Label>{t('state')}</Label>
              <Select value={form.state_key} onValueChange={(v) => updateForm('state_key', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectState')} />
                </SelectTrigger>
                <SelectContent>
                  {states.map(s => (
                    <SelectItem key={s.key} value={s.key}>
                      {lang === 'ar' ? s.name_ar : s.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="form-group">
            <Label htmlFor="duration">{t('opps.fields.duration')}</Label>
            <Input
              id="duration"
              value={form.duration}
              onChange={(e) => updateForm('duration', e.target.value)}
              placeholder={lang === 'ar' ? 'مثال: 3 أشهر' : 'e.g., 3 months'}
            />
          </div>
        </div>

        {/* Requirements */}
        <div className="form-section">
          <div className="form-group">
            <Label>{t('opps.fields.educationLevel')}</Label>
            <Select value={form.education_level} onValueChange={(v) => updateForm('education_level', v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('opps.fields.educationLevel')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high_school">{t('profile.educationLevels.high_school')}</SelectItem>
                <SelectItem value="bachelor">{t('profile.educationLevels.bachelor')}</SelectItem>
                <SelectItem value="master">{t('profile.educationLevels.master')}</SelectItem>
                <SelectItem value="phd">{t('profile.educationLevels.phd')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="form-group">
            <Label>{t('opps.fields.majors')}</Label>
            <div className="majors-chips">
              {subjects.slice(0, 12).map(s => (
                <button
                  key={s.key}
                  type="button"
                  className={`major-chip ${form.majors.includes(s.key) ? 'selected' : ''}`}
                  onClick={() => toggleMajor(s.key)}
                >
                  {lang === 'ar' ? s.name_ar : s.name_en}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <Label>{t('opps.fields.skills')}</Label>
            <div className="tag-input-row">
              <Input
                value={form.skillInput}
                onChange={(e) => updateForm('skillInput', e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                placeholder={t('profile.skillsHint')}
              />
              <Button type="button" variant="outline" onClick={addSkill}>+</Button>
            </div>
            {form.required_skills.length > 0 && (
              <div className="tags-display">
                {form.required_skills.map(skill => (
                  <span key={skill} className="tag">
                    {skill}
                    <button type="button" onClick={() => removeSkill(skill)}><XIcon /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <Label>{t('opps.fields.interests')}</Label>
            <div className="tag-input-row">
              <Input
                value={form.interestInput}
                onChange={(e) => updateForm('interestInput', e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                placeholder={t('profile.interestsHint')}
              />
              <Button type="button" variant="outline" onClick={addInterest}>+</Button>
            </div>
            {form.interests.length > 0 && (
              <div className="tags-display">
                {form.interests.map(interest => (
                  <span key={interest} className="tag">
                    {interest}
                    <button type="button" onClick={() => removeInterest(interest)}><XIcon /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Compensation */}
        <div className="form-section">
          <div className="form-group">
            <div className="switch-row">
              <Label>{t('opps.fields.paid')}</Label>
              <Switch
                checked={form.is_paid}
                onCheckedChange={(v) => updateForm('is_paid', v)}
              />
            </div>
          </div>

          {form.is_paid && (
            <div className="form-row">
              <div className="form-group flex-2">
                <Label htmlFor="stipend">{t('opps.fields.stipend')}</Label>
                <Input
                  id="stipend"
                  type="number"
                  value={form.stipend}
                  onChange={(e) => updateForm('stipend', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div className="form-group flex-1">
                <Label>{t('books.fields.currency')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="currency-btn"
                  onClick={() => setShowCurrencySelect(true)}
                >
                  {form.currency_code}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Application */}
        <div className="form-section">
          <div className="form-group">
            <Label>{t('opps.fields.apply')}</Label>
            <div className="type-toggle">
              <button
                type="button"
                className={`type-btn ${form.apply_method === 'in_app' ? 'active' : ''}`}
                onClick={() => updateForm('apply_method', 'in_app')}
              >
                {lang === 'ar' ? 'داخل التطبيق' : 'In-App'}
              </button>
              <button
                type="button"
                className={`type-btn ${form.apply_method === 'external' ? 'active' : ''}`}
                onClick={() => updateForm('apply_method', 'external')}
              >
                {lang === 'ar' ? 'رابط خارجي' : 'External Link'}
              </button>
            </div>
          </div>

          {form.apply_method === 'external' && (
            <div className="form-group">
              <Label htmlFor="apply_link">{t('opps.fields.applyLink')}</Label>
              <Input
                id="apply_link"
                type="url"
                value={form.apply_link}
                onChange={(e) => updateForm('apply_link', e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="form-group">
            <Label htmlFor="deadline">{t('opps.fields.deadline')}</Label>
            <Input
              id="deadline"
              type="date"
              value={form.deadline}
              onChange={(e) => updateForm('deadline', e.target.value)}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="form-submit">
          <Button 
            type="submit" 
            className="submit-btn"
            disabled={submitting || !form.org_name || !form.title || !form.description}
          >
            {submitting ? t('loading') : t('studentHub.cta.postOpportunity')}
          </Button>
        </div>
      </form>

      {/* Currency Select Modal */}
      {showCurrencySelect && (
        <CurrencySelect
          value={form.currency_code}
          onChange={(code) => {
            updateForm('currency_code', code);
            setShowCurrencySelect(false);
          }}
          onClose={() => setShowCurrencySelect(false)}
        />
      )}

      {/* Exit Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('exitTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('exitMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={`modalActions ${isRTL ? 'flex-row-reverse' : ''}`}>
            <AlertDialogCancel className="btn btnSecondaryOrange">{t('exitCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (hasInAppHistory()) navigate(-1); else navigate('/student-hub', { replace: true }); }} className="btn btnPrimaryOrange">
              {t('exitConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
